import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import TeclaEscEnter from "../../utils/Teclaescenter";
import { API_URL } from "../../utils/config";
import AjudaMestres from "./AjudaMestres";
import { BotoAjuda } from "./AjudaModal";

function Detallsgrupsmaobra({
  mode,
  grup,
  grupsLlista,
  onNavigate,
  onSave,
  onCancel,
  onCanviarMode,
  onDeleted,
}) {
  const isReadOnly = mode === "consultar" || mode === "esborrar";
  TeclaEscEnter(onCancel, null);
  const { empresaSeleccionada, rolUsuari } = useUserContext();

  const [nom, setNom] = useState("");
  const [descripcio, setDescripcio] = useState("");
  const [linies, setLinies] = useState([]);
  const [tipusList, setTipusList] = useState([]);
  const [menuAccions, setMenuAccions] = useState(false);
  const [mostraAjuda, setMostraAjuda] = useState(false);

  const resetForm = useCallback(() => {
    setNom("");
    setDescripcio("");
    setLinies([]);
  }, []);

  useEffect(() => {
    const fetchTipus = async () => {
      if (!empresaSeleccionada?.IdEmpresa) return;
      try {
        const resp = await fetch(`${API_URL}/Tipusmaobra.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idEmpresa: empresaSeleccionada.IdEmpresa }),
        });
        const data = await resp.json();
        setTipusList(data.success ? data.tipusMaObra || [] : []);
      } catch (err) {
        console.error("Error carregant tipus de mà d'obra:", err);
        setTipusList([]);
      }
    };
    fetchTipus();
  }, [empresaSeleccionada]);

  useEffect(() => {
    if (grup && mode !== "nou") {
      setNom(grup.Nom || "");
      setDescripcio(grup.Descripcio || "");
      setLinies(
        (grup.linies || []).map((l) => ({
          idTipusMaObra: l.idTipusMaObra != null ? String(l.idTipusMaObra) : "",
          Quantitat: l.Quantitat ?? 1,
          Tipus: l.Tipus || "",
          PreuHoraBase: l.PreuHoraBase ?? 0,
          Actiu: l.Actiu,
        }))
      );
    } else if (mode === "nou") {
      resetForm();
    }
    setMenuAccions(false);
  }, [grup, mode, resetForm]);

  const llista = Array.isArray(grupsLlista) ? grupsLlista : [];
  const indexActual = grup
    ? llista.findIndex((g) => Number(g.idGrupMaObra) === Number(grup.idGrupMaObra))
    : -1;
  const potNavegar = ["consultar", "modificar"].includes(mode) && llista.length > 1 && indexActual >= 0;

  const anarAnterior = () => {
    if (!potNavegar) return;
    const nouIndex = indexActual === 0 ? llista.length - 1 : indexActual - 1;
    onNavigate?.(llista[nouIndex]);
  };

  const anarSeguent = () => {
    if (!potNavegar) return;
    const nouIndex = indexActual === llista.length - 1 ? 0 : indexActual + 1;
    onNavigate?.(llista[nouIndex]);
  };

  const completarLinia = (linia, idTipusMaObra) => {
    const t = tipusList.find((x) => String(x.idTipusMaObra) === String(idTipusMaObra));
    return {
      ...linia,
      idTipusMaObra: idTipusMaObra ? String(idTipusMaObra) : "",
      Tipus: t?.Tipus || "",
      PreuHoraBase: t?.PreuHoraBase ?? 0,
      Actiu: t?.Actiu,
    };
  };

  const handleLiniaChange = (index, field, value) => {
    setLinies((prev) => {
      const next = [...prev];
      if (field === "idTipusMaObra") {
        next[index] = completarLinia(next[index], value);
      } else {
        next[index] = { ...next[index], [field]: value };
      }
      return next;
    });
  };

  const handleAddRow = () => {
    setLinies((prev) => [
      ...prev,
      { idTipusMaObra: "", Quantitat: 1, Tipus: "", PreuHoraBase: 0, Actiu: 1 },
    ]);
  };

  const handleDeleteRow = (index) => {
    setLinies((prev) => prev.filter((_, i) => i !== index));
  };

  const idsSeleccionats = useMemo(
    () => new Set(linies.map((l) => String(l.idTipusMaObra)).filter(Boolean)),
    [linies]
  );

  const opcionsTipus = (idActual) =>
    tipusList.filter((t) => {
      const id = String(t.idTipusMaObra);
      const esActual = id === String(idActual);
      if (esActual) return true;
      if (idsSeleccionats.has(id)) return false;
      return Number(t.Actiu) === 1;
    });

  const totals = useMemo(() => {
    return linies.reduce(
      (acc, l) => {
        const qty = parseInt(l.Quantitat, 10);
        const nQty = Number.isNaN(qty) ? 0 : qty;
        const preu = parseFloat(l.PreuHoraBase) || 0;
        acc.numEmpleats += nQty;
        acc.preuGrup += preu * nQty;
        return acc;
      },
      { numEmpleats: 0, preuGrup: 0 }
    );
  }, [linies]);

  const validar = () => {
    const errors = [];
    if (!nom.trim()) errors.push("El camp Nom és obligatori.");
    linies.forEach((l, i) => {
      if (!l.idTipusMaObra) return;
      const qty = parseInt(l.Quantitat, 10);
      if (!qty || qty < 1) errors.push(`La línia ${i + 1} ha de tenir quantitat d'almenys 1.`);
    });
    const ids = linies.map((l) => l.idTipusMaObra).filter(Boolean);
    if (new Set(ids).size !== ids.length) {
      errors.push("Un mateix tipus no es pot repetir dins del grup.");
    }
    return errors;
  };

  const liniesPayload = () =>
    linies
      .filter((l) => l.idTipusMaObra)
      .map((l) => ({
        idTipusMaObra: Number(l.idTipusMaObra),
        Quantitat: parseInt(l.Quantitat, 10),
      }));

  const handleSubmit = async () => {
    if (mode !== "esborrar") {
      const errors = validar();
      if (errors.length > 0) {
        alert(`Errors trobats:\n${errors.join("\n")}`);
        return;
      }
    }

    const body = {
      idEmpresa: empresaSeleccionada?.IdEmpresa || null,
      Nom: nom.trim(),
      Descripcio: descripcio.trim(),
      linies: liniesPayload(),
    };

    try {
      if (mode === "nou") {
        const resp = await fetch(`${API_URL}/Nougrupmaobra.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const data = await resp.json();
        if (data.success) {
          onSave?.({
            idGrupMaObra: data.idGrupMaObra,
            Nom: body.Nom,
            Descripcio: body.Descripcio,
            linies: linies.filter((l) => l.idTipusMaObra),
            PreuGrup: totals.preuGrup,
            NumEmpleats: totals.numEmpleats,
          });
        } else {
          alert(data.message || "Error en crear el grup.");
        }
      } else if (mode === "modificar") {
        const resp = await fetch(`${API_URL}/Modificargrupmaobra.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...body, idGrupMaObra: grup.idGrupMaObra }),
        });
        const data = await resp.json();
        if (data.success) {
          onSave?.({
            ...grup,
            Nom: body.Nom,
            Descripcio: body.Descripcio,
            linies: linies.filter((l) => l.idTipusMaObra),
            PreuGrup: totals.preuGrup,
            NumEmpleats: totals.numEmpleats,
          });
        } else {
          alert(data.message || "Error en modificar el grup.");
        }
      } else if (mode === "esborrar") {
        const resp = await fetch(`${API_URL}/Esborrargrupmaobra.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            idEmpresa: empresaSeleccionada?.IdEmpresa || null,
            idGrupMaObra: grup.idGrupMaObra,
          }),
        });
        const data = await resp.json();
        if (data.success) {
          onDeleted?.();
        } else {
          alert(data.message || "Error en esborrar el grup.");
        }
      }
    } catch (err) {
      console.error(err);
      alert("S'ha produït un error en el procés.");
    }
  };

  const titol =
    mode === "nou"
      ? "Nou grup de mà d'obra"
      : mode === "esborrar"
      ? "Esborrar grup de mà d'obra"
      : mode === "modificar"
      ? "Modificar grup de mà d'obra"
      : "Grups de mà d'obra · Fitxa";

  const subtitol =
    mode === "nou"
      ? "Defineix el nom i la composició del grup."
      : mode === "esborrar"
      ? "Els grups són comuns a totes les empreses. Si s'usa a tasques, partides o fulls, no es podrà esborrar."
      : nom || "Dades del grup";

  return (
    <form className="dv2-entity" onSubmit={(e) => e.preventDefault()}>
      {mostraAjuda && <AjudaMestres pestanya="grupsmaobra" onTancar={() => setMostraAjuda(false)} />}
      <div className="dv2-pageHeader">
        <div className="dv2-pageHeader-main">
          <div className={`dv2-pageHeader-icon ${mode === "esborrar" ? "is-danger" : ""}`} aria-hidden="true">
            {mode === "esborrar" ? "🗑️" : "👥"}
          </div>
          <div>
            <h1 className="dv2-pageHeader-title">{titol}</h1>
            <p className="dv2-pageHeader-subtitle">{subtitol}</p>
          </div>
        </div>
        <div className="dv2-pageHeader-actions">
          <BotoAjuda title="Com funcionen els Grups de mà d'obra" onClick={() => setMostraAjuda(true)} />
          <button type="button" className="dv2-btn" onClick={onCancel}>
            Tornar
          </button>
          {mode === "consultar" && tincPermis(rolUsuari, "MODIFICAR") && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={() => onCanviarMode?.("modificar")}>
              Modificar
            </button>
          )}
          {mode === "consultar" && tincPermis(rolUsuari, "ESBORRAR") && (
            <div style={{ position: "relative" }}>
              <button type="button" className="dv2-btn" onClick={() => setMenuAccions((v) => !v)}>
                Més accions
              </button>
              {menuAccions && (
                <div className="dv2-menu" style={{ right: 0, top: "42px" }}>
                  <button
                    type="button"
                    className="dv2-menu-item is-danger"
                    onClick={() => {
                      setMenuAccions(false);
                      onCanviarMode?.("esborrar");
                    }}
                  >
                    Esborrar
                  </button>
                </div>
              )}
            </div>
          )}
          {(mode === "nou" || mode === "modificar") && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleSubmit}>
              Desar
            </button>
          )}
          {mode === "esborrar" && (
            <button type="button" className="dv2-btn dv2-btn--danger" onClick={handleSubmit}>
              Esborrar grup
            </button>
          )}
        </div>
      </div>

      {mode === "esborrar" && (
        <div className="dv2-banner dv2-banner--danger">
          Aquesta acció no es pot desfer. Si el grup s'usa a tasques, partides o fulls de treball, el servidor no el deixarà esborrar.
        </div>
      )}

      <div className="dv2-entity-body">
        <section className="dv2-card">
          <div className="dv2-card-head">
            <span className="dv2-card-num">1</span>
            <h2 className="dv2-card-title">Dades bàsiques</h2>
          </div>
          <div className="dv2-fields dv2-fields--sizes">
            <label className="dv2-field dv2-field--llarg">
              <span className={`dv2-label ${isReadOnly ? "" : "dv2-required"}`}>Nom</span>
              {isReadOnly ? (
                <span className="dv2-value">{nom || "—"}</span>
              ) : (
                <input type="text" className="dv2-input" value={nom} maxLength={45} autoFocus onChange={(e) => setNom(e.target.value)} />
              )}
            </label>
            <label className="dv2-field dv2-field--ample">
              <span className="dv2-label">Descripció</span>
              {isReadOnly ? (
                <span className="dv2-value">{descripcio || "—"}</span>
              ) : (
                <input type="text" className="dv2-input" value={descripcio} onChange={(e) => setDescripcio(e.target.value)} />
              )}
            </label>
          </div>
        </section>

        <section className="dv2-card">
          <div className="dv2-card-head">
            <span className="dv2-card-num">2</span>
            <h2 className="dv2-card-title">Composició</h2>
          </div>
          {isReadOnly ? (
            linies.length === 0 ? (
              <p className="dv2-empty">Aquesta empresa encara no té tipus assignats a aquest grup.</p>
            ) : (
              <div className="dv2-specList">
                {linies.map((l, i) => (
                  <div key={`${l.idTipusMaObra}-${i}`} className="dv2-spec">
                    <span className="dv2-spec-name">
                      {l.Quantitat} × {l.Tipus || "—"}
                      {Number(l.Actiu) === 0 ? " (inactiu)" : ""}
                    </span>
                    <span className="dv2-spec-value">{(parseFloat(l.PreuHoraBase) || 0).toFixed(2)} €/h</span>
                  </div>
                ))}
                <div className="dv2-spec">
                  <span className="dv2-spec-name">Persones</span>
                  <span className="dv2-spec-value">{totals.numEmpleats}</span>
                </div>
                <div className="dv2-spec">
                  <span className="dv2-spec-name">Preu grup</span>
                  <span className="dv2-spec-value">{totals.preuGrup.toFixed(2)} €/h</span>
                </div>
              </div>
            )
          ) : (
            <>
              {linies.map((l, index) => {
                const qty = parseInt(l.Quantitat, 10) || 0;
                const preu = parseFloat(l.PreuHoraBase) || 0;
                return (
                  <div
                    key={index}
                    className="dv2-editorRow"
                    style={{ gridTemplateColumns: "90px minmax(180px, 1.4fr) 110px 110px 40px" }}
                  >
                    <label className="dv2-field">
                      <span className="dv2-label">Quantitat</span>
                      <input
                        type="number"
                        min="1"
                        className="dv2-input"
                        value={l.Quantitat}
                        onChange={(e) => handleLiniaChange(index, "Quantitat", e.target.value)}
                      />
                    </label>
                    <label className="dv2-field">
                      <span className="dv2-label">Tipus</span>
                      <select
                        className="dv2-select"
                        value={l.idTipusMaObra || ""}
                        onChange={(e) => handleLiniaChange(index, "idTipusMaObra", e.target.value)}
                      >
                        <option value="">— Selecciona tipus —</option>
                        {opcionsTipus(l.idTipusMaObra).map((t) => (
                          <option key={t.idTipusMaObra} value={String(t.idTipusMaObra)}>
                            {t.Tipus}
                            {Number(t.Actiu) === 0 ? " (inactiu)" : ""}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="dv2-field">
                      <span className="dv2-label">Preu hora</span>
                      <span className="dv2-value">{preu.toFixed(2)} €/h</span>
                    </label>
                    <label className="dv2-field">
                      <span className="dv2-label">Subtotal</span>
                      <span className="dv2-value">{(preu * qty).toFixed(2)} €</span>
                    </label>
                    <button
                      type="button"
                      className="dv2-btn dv2-btn--ghost dv2-btn--icon"
                      onClick={() => handleDeleteRow(index)}
                      title="Treure línia"
                    >
                      ✖
                    </button>
                  </div>
                );
              })}
              <div className="dv2-controlRow" style={{ marginTop: 10 }}>
                <button type="button" className="dv2-btn dv2-btn--compact" onClick={handleAddRow}>
                  + Afegir tipus
                </button>
                <span className="dv2-hint">
                  Persones: {totals.numEmpleats} · Preu grup: {totals.preuGrup.toFixed(2)} €/h
                </span>
              </div>
            </>
          )}
        </section>
      </div>

      {(potNavegar || mode === "nou" || mode === "modificar" || mode === "esborrar") && (
        <div className="dv2-entity-dock">
          {potNavegar && (
            <div className="dv2-recordNav">
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={anarAnterior} title="Anterior">
                ‹
              </button>
              <span className="dv2-recordNav-info">
                {indexActual + 1} / {llista.length}
              </span>
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={anarSeguent} title="Següent">
                ›
              </button>
            </div>
          )}
          {(mode === "nou" || mode === "modificar" || mode === "esborrar") && (
            <div className="dv2-footerActions">
              <button type="button" className="dv2-btn" onClick={onCancel}>
                Cancel·lar
              </button>
              {mode === "esborrar" ? (
                <button type="button" className="dv2-btn dv2-btn--danger" onClick={handleSubmit}>
                  Esborrar grup
                </button>
              ) : (
                <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleSubmit}>
                  {mode === "nou" ? "Desar grup" : "Desar"}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </form>
  );
}

export default Detallsgrupsmaobra;
